import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Mail, Send, Eye, RefreshCw, Megaphone, Users, CheckCircle2, XCircle,
  Loader2, MailOpen, MousePointerClick, AlertTriangle, ShieldOff,
} from "lucide-react";

const PREVIEW_URL = `${process.env.REACT_APP_BACKEND_URL || ""}/api/admin/digest/preview`;

function Metric({ label, value, subtitle, icon: Icon, color, highlight }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        highlight
          ? "bg-[var(--tt-primary)]/10 border-[var(--tt-primary)]/40"
          : "bg-[var(--tt-surface)] border-[var(--tt-border)]"
      }`}
      data-testid={`admin-digest-metric-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        {label}
      </div>
      <div className="font-display text-2xl font-extrabold text-white tabular-nums">
        {value ?? "—"}
      </div>
      {subtitle ? (
        <div className="text-[10px] mt-0.5 font-mono-tt" style={{ color }}>{subtitle}</div>
      ) : (
        <div className="text-[10px] mt-0.5 text-[var(--tt-text-faint)]">&nbsp;</div>
      )}
    </div>
  );
}

export default function AdminDigest() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testEmail, setTestEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [broadcasting, setBroadcasting] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/digest/stats");
      setStats(data);
    } catch {
      toast.error("Failed to load digest stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const sendTest = async () => {
    if (!testEmail || !testEmail.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/admin/digest/test-send", { email: testEmail });
      toast.success(`Test digest sent (id: ${(data.email_id || "").slice(0, 8)}…)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const broadcast = async () => {
    if (!stats?.opted_in_count) {
      toast.error("No opted-in users to broadcast to");
      return;
    }
    const ok = window.confirm(
      `Broadcast the weekly digest to ${stats.opted_in_count} opted-in user(s) RIGHT NOW?\n\nThis bypasses the Monday 09:00 UTC schedule.`
    );
    if (!ok) return;
    setBroadcasting(true);
    try {
      const { data } = await api.post("/admin/digest/broadcast");
      toast.success(`Broadcast complete · ${data.sent} sent · ${data.failed} failed`);
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setBroadcasting(false);
    }
  };

  return (
    <section className="mt-10" data-testid="admin-digest-section">
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h2 className="font-display text-xl font-bold flex items-center gap-2">
          <Mail className="w-5 h-5 text-[var(--tt-primary)]" />
          Weekly Trending Digest
        </h2>
        <span className="text-[10px] uppercase tracking-wider bg-[var(--tt-primary)]/15 text-[var(--tt-primary)] px-2 py-0.5 rounded font-semibold">
          Resend · Mon 09:00 UTC
        </span>
        <button
          onClick={reload}
          disabled={loading}
          data-testid="admin-digest-refresh"
          className="ml-auto text-xs font-semibold px-3 py-1.5 rounded-full bg-[var(--tt-surface-2)] hover:bg-white/10 text-[var(--tt-text-muted)] hover:text-white flex items-center gap-1.5"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-4">
        {/* Opted-in count */}
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">
            <Users className="w-3.5 h-3.5" /> Opted-in subscribers
          </div>
          <div className="font-display text-3xl font-extrabold text-white" data-testid="admin-digest-opted-in">
            {stats?.opted_in_count ?? "—"}
          </div>
          <div className="text-[11px] text-[var(--tt-text-faint)] mt-1">
            Users with the toggle ON in Settings → Email
          </div>
        </div>

        {/* Sender / config */}
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">
            <Mail className="w-3.5 h-3.5" /> Sender
          </div>
          <div className="font-mono-tt text-sm text-white truncate" data-testid="admin-digest-sender" title={stats?.sender_email}>
            {stats?.sender_email || "—"}
          </div>
          <div className="text-[11px] mt-2 flex items-center gap-2 flex-wrap">
            {stats?.resend_configured ? (
              <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Resend API</span>
            ) : (
              <span className="text-red-400 flex items-center gap-1"><XCircle className="w-3.5 h-3.5" /> Missing API key</span>
            )}
            {stats?.webhook_configured ? (
              <span className="text-emerald-400 flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> Webhook signed</span>
            ) : (
              <span className="text-[var(--tt-yellow)] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> Webhook unsigned</span>
            )}
          </div>
        </div>

        {/* Preview link */}
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)] mb-1">
            <Eye className="w-3.5 h-3.5" /> Preview
          </div>
          <a
            href={PREVIEW_URL}
            target="_blank"
            rel="noreferrer"
            data-testid="admin-digest-preview-link"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[var(--tt-primary)] hover:text-[var(--tt-primary-strong)]"
          >
            Open preview tab <Eye className="w-4 h-4" />
          </a>
          <div className="text-[11px] text-[var(--tt-text-faint)] mt-2">
            See exactly what subscribers will receive (uses your own watchlist).
          </div>
        </div>
      </div>

      {/* Engagement metrics row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4" data-testid="admin-digest-engagement">
        <Metric
          label="Sent"
          value={stats?.engagement?.total_sent}
          icon={Send}
          color="#a0a0a0"
        />
        <Metric
          label="Delivered"
          value={stats?.engagement?.delivered}
          subtitle={stats?.engagement?.delivery_rate_pct != null ? `${stats.engagement.delivery_rate_pct}%` : null}
          icon={CheckCircle2}
          color="#22c55e"
        />
        <Metric
          label="Opened"
          value={stats?.engagement?.opened_unique}
          subtitle={stats?.engagement?.open_rate_pct != null ? `${stats.engagement.open_rate_pct}% open rate` : null}
          icon={MailOpen}
          color="#ff2a6d"
          highlight
        />
        <Metric
          label="Clicked"
          value={stats?.engagement?.clicked_unique}
          subtitle={stats?.engagement?.click_rate_pct != null ? `${stats.engagement.click_rate_pct}% CTR` : null}
          icon={MousePointerClick}
          color="#f9a826"
        />
        <Metric
          label="Bounced / Spam"
          value={(stats?.engagement?.bounced || 0) + (stats?.engagement?.complained || 0)}
          subtitle={stats?.engagement?.complained ? `${stats.engagement.complained} spam` : null}
          icon={ShieldOff}
          color="#ef4444"
        />
      </div>

      {/* Setup banner — show only if webhook secret missing */}
      {stats && !stats.webhook_configured && (
        <div className="mb-4 bg-[var(--tt-yellow)]/10 border border-[var(--tt-yellow)]/30 rounded-2xl px-4 py-3 text-xs text-[var(--tt-yellow)] flex items-start gap-3" data-testid="admin-digest-webhook-banner">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-none" />
          <div>
            <div className="font-bold mb-1">Add a Resend webhook to track opens &amp; clicks</div>
            <div className="text-white/80 leading-relaxed">
              Go to <a href="https://resend.com/webhooks" target="_blank" rel="noreferrer" className="underline hover:text-white">resend.com/webhooks</a>, point a new endpoint at <span className="font-mono-tt bg-black/30 px-1.5 py-0.5 rounded">{(process.env.REACT_APP_BACKEND_URL || "") + "/api/webhooks/resend"}</span>,
              copy its Signing Secret, and set <span className="font-mono-tt bg-black/30 px-1.5 py-0.5 rounded">RESEND_WEBHOOK_SECRET</span> in backend env. Then redeploy.
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2"><Send className="w-4 h-4 text-[var(--tt-yellow)]" /> Test send</h3>
          <p className="text-[11px] text-[var(--tt-text-muted)] mb-3">
            Drop your address (or <span className="font-mono-tt">delivered@resend.dev</span>) — we&apos;ll send a single digest using your watchlist data.
          </p>
          <div className="flex gap-2">
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="admin-digest-test-email"
              className="flex-1 bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded-lg px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)]"
            />
            <button
              onClick={sendTest}
              disabled={sending || !stats?.resend_configured}
              data-testid="admin-digest-send-test"
              className="text-sm font-bold px-4 py-2 rounded-lg bg-[var(--tt-yellow)] text-black hover:opacity-90 disabled:opacity-40 flex items-center gap-2"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send
            </button>
          </div>
        </div>

        <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-5">
          <h3 className="text-sm font-bold mb-2 flex items-center gap-2"><Megaphone className="w-4 h-4 text-[var(--tt-primary)]" /> Broadcast now</h3>
          <p className="text-[11px] text-[var(--tt-text-muted)] mb-3">
            Manually trigger the weekly send. Useful for catch-up runs or before a feature launch.
          </p>
          <button
            onClick={broadcast}
            disabled={broadcasting || !stats?.opted_in_count || !stats?.resend_configured}
            data-testid="admin-digest-broadcast"
            className="w-full text-sm font-bold px-4 py-2.5 rounded-lg bg-[var(--tt-primary)] text-white hover:bg-[var(--tt-primary-strong)] disabled:opacity-40 flex items-center justify-center gap-2"
          >
            {broadcasting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            Broadcast to {stats?.opted_in_count ?? 0} subscriber{stats?.opted_in_count === 1 ? "" : "s"}
          </button>
        </div>
      </div>

      {/* Recent runs */}
      <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[var(--tt-border)] flex items-center gap-2">
          <h3 className="text-sm font-bold">Recent runs</h3>
          <span className="text-[10px] font-mono-tt bg-[var(--tt-surface-2)] text-[var(--tt-text-muted)] px-2 py-0.5 rounded">
            {stats?.last_runs?.length ?? 0}
          </span>
        </div>
        {stats?.last_runs?.length ? (
          <ul className="divide-y divide-[var(--tt-border)] max-h-64 overflow-y-auto" data-testid="admin-digest-runs">
            {stats.last_runs.map((r, i) => (
              <li key={i} className="px-4 py-3 text-xs flex items-center gap-3 flex-wrap" data-testid={`admin-digest-run-${i}`}>
                <span className="font-mono-tt text-[10px] text-[var(--tt-text-muted)]">
                  {r.ts?.slice(0, 19).replace("T", " ")}
                </span>
                <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-emerald-500/15 text-emerald-400">
                  {r.sent} sent
                </span>
                {r.failed > 0 && (
                  <span className="text-[10px] uppercase font-bold px-2 py-0.5 rounded bg-red-500/15 text-red-400">
                    {r.failed} failed
                  </span>
                )}
                <span className="text-[var(--tt-text-muted)]">· {r.recipients} recipient{r.recipients === 1 ? "" : "s"}</span>
                {r.errors_sample?.length ? (
                  <div className="basis-full text-[10px] text-red-400/80 mt-1 font-mono-tt break-words">
                    {r.errors_sample.join(" · ")}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        ) : (
          <div className="py-8 text-center text-[var(--tt-text-muted)] text-sm">No broadcasts yet.</div>
        )}
      </div>
    </section>
  );
}
